import React, { useState, useEffect } from 'react';
import { Field, Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import {
  ComboBox,
  Button,
  Toggler,
  TextInput,
  InputMaskField,
  Datepicker,
  CollapseWithLabel,
  Radio,
} from '@components';
import {
  dependentesIRSchema,
  dependentesIRInitialValues,
  dependentesIRDocs,
} from '@schemas';
import {
  criaFormRascunhoRequest,
  adicionaItemRascunhoRequest,
  atualizaItemRascunhoRequest,
  excluiItemRascunhoRequest,
  concluirFormRequest,
} from '@store/user/actions';
import { useNavigate } from 'react-router-dom';
import { parentescoSituacao } from '@utils';
import { ModalUpload } from './components/ModalUpload';
import { validaUpload } from './utils/validaUpload';
import { rulesSituationParents } from '../../../../common/schemas/requerimentos/utils/rulesByKey';

const Anexos = ({ disabled, reset, cpf, parentesco }) => {
  const docs = dependentesIRDocs;
  const anexosArray = docs[parentesco];
  const dependentes = useSelector((state) => state.user.formularioLista);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  return (
    <>
      <ModalUpload modalIsOpen={modalIsOpen} setModalIsOpen={setModalIsOpen} />
      {docs.commons.map((anexo) => (
        <div className="row" key={anexo.name}>
          <div className="col-md-12 col-lg-6">
            <Field
              className="mb-3"
              name={anexo.name}
              type="file"
              label={anexo.label}
              accept="image/*,.pdf"
            >
              {({ form }) => {
                const itemAtual = dependentes.find((dp) => dp.cpf === cpf);
                const anexoAtual = itemAtual?.anexos.find(
                  (anx) => anx.name === anexo.name
                );

                return (
                  <TextInput
                    key={reset ? 'reset' : ''}
                    className="mb-3"
                    name={anexo.name}
                    type="file"
                    label={anexo.label}
                    accept="image/*,.pdf"
                    multiple
                    onChange={(e) => {
                      return validaUpload({
                        anexo,
                        e,
                        form,
                        setModalIsOpen,
                      });
                    }}
                    disabled={disabled}
                    defaultFile={anexoAtual}
                  />
                );
              }}
            </Field>
          </div>
        </div>
      ))}
      {(anexosArray || []).map((anexo) => (
        <div className="row" key={anexo.name}>
          <div className="col-md-12 col-lg-6">
            <Field
              className="mb-3"
              name={anexo.name}
              type="file"
              label={anexo.label}
              accept="image/*,.pdf"
            >
              {({ form }) => {
                return (
                  <TextInput
                    className="mb-3"
                    name={anexo.name}
                    type="file"
                    label={anexo.label}
                    accept="image/*,.pdf"
                    multiple
                    onChange={(e) => {
                      return validaUpload({
                        anexo,
                        e,
                        form,
                        setModalIsOpen,
                      });
                    }}
                  />
                );
              }}
            </Field>
          </div>
        </div>
      ))}
    </>
  );
};

const CardDependente = ({
  nascimento,
  dependente,
  cpf,
  maeDoDependente,
  paiDoDependente,
  parentesco,
  situacaoParentesco,
  consentimentoDadosSensiveis,
  anexos,
  acao,

  setDisabled,
}) => {
  const formattedInitialAnexos = anexos.reduce(
    (previousValue, currentValue) => {
      return { ...previousValue, [currentValue.name]: currentValue };
    },
    {}
  );

  const initial = {
    nascimento,
    dependente,
    cpf,
    maeDoDependente,
    paiDoDependente,
    parentesco,
    situacaoParentesco,
    consentimentoDadosSensiveis,
    acao,
    ...formattedInitialAnexos,
  };

  return (
    <>
      <hr className="mt-32 mt-xs-0 mb-32" />
      <CollapseWithLabel
        label={
          <>
            <strong>Dependente: </strong>
            {dependente}
          </>
        }
      >
        <FormikBody type="update" initial={initial} setDisabled={setDisabled} />
      </CollapseWithLabel>
    </>
  );
};

const FormikBody = ({ initial, type, setDisabled }) => {
  const dispatch = useDispatch();
  const isUpdate = type === 'update';
  const [isEditing, setIsEditing] = useState(!isUpdate);
  const [calledReset, setCalledReset] = useState(false);

  useEffect(() => {
    setDisabled(isEditing);
  }, [isEditing, setDisabled]);

  return (
    <>
      <hr className="mt-32 mt-xs-0 mb-32" />
      <Formik
        validateOnMount
        initialValues={initial || dependentesIRInitialValues}
        validationSchema={dependentesIRSchema}
        onSubmit={(values, { resetForm }) => {
          setDisabled(false);

          const successMessage = 'Dependente adicionado com sucesso';

          if (isUpdate) {
            const nascimento =
              values?.nascimento?.toLocaleDateString('pt') || values.nascimento;

            dispatch(
              atualizaItemRascunhoRequest({
                campos: {
                  ...values,
                  nascimento,
                },
                camposAntigos: initial,
                successMessage: successMessage.replace(
                  'adicionado',
                  'atualizado'
                ),
              })
            );

            setIsEditing(false);
          } else {
            const nascimento = values.nascimento
              ? values?.nascimento?.toLocaleDateString('pt')
              : values.nascimento;

            dispatch(
              adicionaItemRascunhoRequest({
                campos: {
                  ...values,
                  nascimento,
                },
                successMessage,
              })
            );
            resetForm();
            setCalledReset(true);
            setTimeout(() => {
              setCalledReset(false);
            }, 150);
          }
        }}
      >
        {({ handleSubmit, isValid, values, resetForm, setFieldValue }) => (
          <Form>
            <div className="row mb-4">
              <div className="col-md-12 col-lg-6">
                <p className="mb-4">Ação</p>
                <Field
                  className="me-3"
                  name="acao"
                  color="orange"
                  type="radio"
                  label="Incluir"
                  component={Radio}
                  value="Incluir"
                  disabled={!isEditing}
                />
                <Field
                  className="me-3"
                  type="radio"
                  color="orange"
                  name="acao"
                  label="Alterar"
                  component={Radio}
                  value="Alterar"
                  disabled={!isEditing}
                />
                <Field
                  type="radio"
                  color="orange"
                  name="acao"
                  label="Excluir"
                  component={Radio}
                  value="Excluir"
                  disabled={!isEditing}
                  onChange={(e) => {
                    resetForm();
                    setFieldValue('acao', e.target.value, false);
                  }}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-6">
                <Field
                  className="mb-3"
                  name="dependente"
                  component={TextInput}
                  label="Nome completo do (a) Dependente"
                  disabled={!isEditing}
                />
              </div>
              {values.acao && values.acao !== 'Excluir' && (
                <div className="col-md-12 col-lg-6">
                  <Field
                    className="mb-3"
                    name="cpf"
                    mask="111.111.111-11"
                    inputMode="numeric"
                    component={InputMaskField}
                    label="CPF Dependente"
                    disabled={!isEditing}
                  />
                </div>
              )}
            </div>

            {values.acao && values.acao !== 'Excluir' && (
              <div className="row mb-3">
                <div className="col-md-12 col-lg-6">
                  <Field
                    name="nascimento"
                    component={Datepicker}
                    datePickerClassName="formSize"
                    max={new Date()}
                    label="Data de Nascimento do Dependente"
                    disabled={!isEditing}
                    showYearDropdown
                    yearDropdownItemNumber={100}
                    scrollableYearDropdown
                  />
                </div>
              </div>
            )}

            {values.acao && values.acao !== 'Excluir' && (
              <div className="row">
                <div className="col-md-12 col-lg-6">
                  <Field
                    className="mb-3"
                    name="maeDoDependente"
                    component={TextInput}
                    label="Nome completo da Mãe do Dependente"
                    disabled={!isEditing}
                  />
                </div>
                <div className="col-md-12 col-lg-6">
                  <Field
                    className="mb-3"
                    name="paiDoDependente"
                    component={TextInput}
                    label="Nome completo do Pai do Dependente"
                    disabled={!isEditing}
                  />
                </div>

                {values.acao && values.acao !== 'Excluir' && (
                  <div className="row">
                    <div className="col-md-12 col-lg-6">
                      <Field
                        className="mb-3"
                        name="parentesco"
                        component={ComboBox}
                        onChange={() => {
                          setFieldValue('situacaoParentesco', '', true);
                        }}
                        options={parentescoSituacao.options.parentescoOptions.map(
                          (x) => {
                            return { value: x, label: x };
                          }
                        )}
                        label="Parentesco/Vínculo"
                        disabled={!isEditing}
                      />
                    </div>

                    {values.acao &&
                      values.acao !== 'Excluir' &&
                      rulesSituationParents.includes(values.parentesco) && (
                        <div className="col-md-12 col-lg-6">
                          <Field
                            className="mb-3 ms-2"
                            name="situacaoParentesco"
                            component={ComboBox}
                            options={parentescoSituacao.options.situacaoOptions.map(
                              (x) => {
                                return { value: x, label: x };
                              }
                            )}
                            label="Situação"
                            disabled={!isEditing}
                          />
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}

            {values.acao && values.acao !== 'Excluir' && (
              <div className="row">
                <div className="col-md-12 col-lg-6">
                  <Field
                    className="mb-3"
                    name="consentimentoDadosSensiveis"
                    component={Toggler}
                    label="Consentimento dados sensíveis (obrigatório)"
                    disabled={!isEditing}
                  />
                </div>
              </div>
            )}

            {values.acao && values.acao !== 'Excluir' && (
              <Anexos
                disabled={!isEditing}
                reset={calledReset}
                cpf={values.cpf}
                parentesco={
                  values.situacaoParentesco !== ''
                    ? `${values.parentesco}_${values.situacaoParentesco}`
                    : values.parentesco
                }
              />
            )}

            {isEditing ? (
              <>
                <hr className="mt-32 mt-xs-0 mb-32" />
                <div className="row">
                  <div className="col-12 col-md-6 col-xl-4 ms-0 ms-md-auto">
                    <div className="d-block d-md-flex align-items-center">
                      <Button
                        disabled={!isValid}
                        className="ms-0 ms-md-3"
                        onClick={handleSubmit}
                      >
                        {isUpdate ? 'Atualizar' : 'Adicionar'}
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <hr className="mt-32 mt-xs-0 mb-32" />
                <div className="row">
                  <div className="col-12 col-md-6 col-xl-4 ms-0 ms-md-auto">
                    <div className="d-block d-md-flex align-items-center">
                      <Button
                        className="ms-0 ms-md-3"
                        onClick={() => {
                          dispatch(excluiItemRascunhoRequest(values));
                        }}
                      >
                        Eliminar
                      </Button>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-xl-4 ms-0 ms-md-auto">
                    <div className="d-block d-md-flex align-items-center">
                      <Button
                        className="ms-0 ms-md-3"
                        onClick={() => setIsEditing(true)}
                      >
                        Editar
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
};

export const DependentesIR = ({ title, reference }) => {
  const [disabled, setDisabled] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dependentes = useSelector((state) => state.user.formularioLista);

  useEffect(() => {
    dispatch(criaFormRascunhoRequest(reference));
  }, [dispatch, reference]);

  return (
    <div className="card">
      <div className="card-body">
        <h3 className="fw-bold ts-20 mb-32">{title}</h3>

        <p className="mb-4">
          Antes de preencher favor verificar os critérios de dependência
          estabelecidos pela legislação vigente para o Imposto de Renda Retido
          na Fonte, Receita Federal
        </p>
        <FormikBody setDisabled={setDisabled} />

        {dependentes.map((dependente) => {
          return (
            <CardDependente
              key={dependente.dependente}
              {...dependente}
              setDisabled={setDisabled}
            />
          );
        })}
        {dependentes.length > 0 && (
          <>
            <hr className="mt-32 mt-xs-0 mb-32" />
            <div className="row">
              <div className="col-12 col-md-6 col-xl-4 ms-0 ms-md-auto">
                <div className="d-block d-md-flex align-items-center">
                  <Button
                    className="ms-0 ms-md-3"
                    disabled={disabled}
                    onClick={() => {
                      dispatch(concluirFormRequest(reference));
                      navigate('/requerimentos/novo');
                    }}
                  >
                    Solicitar
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
